import { getSidebarRecentItemsSnapshot, hasSidebarRecentItemsError, } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/sidebar-recent-items-state/sidebarRecentItemsState.js';
import { useSidebarRecentsFilter } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/useSidebarRecentsFilter.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useEffect, useRef, useState } from 'react';
const DISABLED_STATE = {
    data: null,
    status: 'disabled',
};
const LOADING_STATE = {
    data: null,
    status: 'loading',
};
function currentState(enabled, filter) {
    if (!enabled)
        return DISABLED_STATE;
    const cachedItems = getSidebarRecentItemsSnapshot(filter);
    // The shared state can hold a kept snapshot alongside an error; a hook
    // mounting mid-outage must not report a healthy live feed.
    if (hasSidebarRecentItemsError(filter))
        return { data: cachedItems, status: 'error' };
    return cachedItems ? { data: cachedItems, status: 'active' } : LOADING_STATE;
}
/**
 * Loads sidebar recents through the shared initial query and live subscription.
 * The manager is dynamically imported so ineligible clients do not load Relay.
 */
export function useSidebarRecentItemsGraphQL({ enabled, filter: requestedFilter, }) {
    const { filter: storedFilter } = useSidebarRecentsFilter(enabled);
    const filter = requestedFilter ?? storedFilter;
    const [state, setState] = useState(() => currentState(enabled, filter));
    const filterRef = useRef(filter);
    useEffect(() => {
        filterRef.current = filter;
        if (!enabled) {
            setState(DISABLED_STATE);
            return undefined;
        }
        setState(currentState(enabled, filter));
        let disposed = false;
        let release;
        import('#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/sidebarLiveUpdatesManager.js')
            .then((module) => {
            if (disposed) {
                return;
            }
            release = module.retainSidebarRecentItems({
                onSnapshot: (items) => setState({ data: items, status: 'active' }),
                // Keep the last rows through a transport error so consumers can
                // render them while the REST fallback loads.
                onError: () => setState((prev) => ({ ...prev, status: 'error' })),
            }, { filter });
        })
            .catch((error) => {
            if (disposed) {
                return;
            }
            logger.error('Failed to load sidebar recent items subscription.', error);
            // Same kept-rows contract as the subscription error path: a failed
            // manager chunk load must not blank rows already on screen.
            setState((prev) => ({ ...prev, status: 'error' }));
        });
        return () => {
            disposed = true;
            release?.();
        };
    }, [enabled, filter]);
    if (!enabled)
        return DISABLED_STATE;
    // Both checks cover the render before the effect re-runs. A just-enabled
    // feed is named by the status, but a just-changed selection is not: the
    // stored rows are a different feed's and would flash as if they were this
    // selection's.
    if (state.status === 'disabled' || filterRef.current !== filter) {
        return currentState(enabled, filter);
    }
    return state;
}
//# sourceMappingURL=useSidebarRecentItemsGraphQL.js.map